/**
 * features/applications/ApplicationDetail.tsx
 * ------------------------------------------------------------------
 * Read-only view of a submitted application plus the decisions panel
 * where reviewers set the class and notes. The Approve
 * button enables only when every required decision is set. Admit
 * calls the admitApplication Cloud Function which clones the
 * application into reportStudents and deletes the application doc.
 *
 * Status flow this UI drives:
 *   pending → under_review (auto on first decisions edit)
 *           → approved     (all decisions set, click Approve)
 *           → admitted     (deleted, becomes a Student)
 *           → rejected     (terminal)
 * ------------------------------------------------------------------
 */
import { useEffect, useMemo, useState } from 'react';
import {
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  deleteField,
} from 'firebase/firestore';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  AlertCircle,
  User,
  Users,
  Stethoscope,
  GraduationCap,
  Phone,
  Mail,
  MessageCircle,
  Loader2,
  UserPlus,
  Edit3,
  Star,
  Globe,
  Briefcase,
  BookOpen,
  Printer,
} from 'lucide-react';
import { db } from '@/lib/firebase';
import { buildApplicationFormPdf } from '@/lib/applicationFormPdf';
import ApplicationDocuments from '@/features/applications/ApplicationDocuments';
import { documentsProblem, type AttachedDocument } from '@/lib/applicationDocuments';
import EntranceAssessmentPanel from '@/features/applications/EntranceAssessmentPanel';
import { assessmentProblem, isOverridingFail } from '@/lib/entranceAssessment';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import { compareClasses } from '@/lib/classSort';
import { useData } from '@/contexts/DataContext';
import AdmitWizard from '@/features/applications/AdmitWizard';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/ui/primitives';
import {
  GradientHero,
  SoftCard,
  AccentButton,
  glassFieldClass,
} from '@/features/finance/ui';
import type {
  ApplicationRecord,
  ApplicationDecisions,
  AdmissionFeeReceipt,
  ApplicationFamilyLink,
  Guardian,
  AdmissionFormText,
} from '@/types';

interface Props {
  applicationId: string;
  onBack: () => void;
}

const GUARDIAN_LABEL = (g: Guardian) =>
  ({
    father: 'Father',
    mother: 'Mother',
    guardian: 'Guardian',
    stepfather: 'Stepfather',
    stepmother: 'Stepmother',
    grandparent: 'Grandparent',
    other: g.roleLabel || 'Other',
  }[g.role] ?? 'Guardian');

export default function ApplicationDetail({ applicationId, onBack }: Props) {
  const { userData } = useAuth();
  const { classes, families } = useData();

  const [app, setApp] = useState<ApplicationRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [actionState, setActionState] = useState<'idle' | 'rejecting'>('idle');
  const [showWizard, setShowWizard] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const [draftDecisions, setDraftDecisions] = useState<ApplicationDecisions>({});
  const [dirty, setDirty] = useState(false);
  const [feeCfg, setFeeCfg] = useState<{ application: number; registration: number }>({ application: 0, registration: 0 });
  const [termPct, setTermPct] = useState(100);
  const [familyQuery, setFamilyQuery] = useState('');
  const [newFamilyName, setNewFamilyName] = useState('');

  /* ---- configured up-front fee amounts (admissions can read finance settings) ---- */
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'financeSettings', 'school'), (snap) => {
      const d = snap.exists() ? snap.data() : {};
      setFeeCfg({ application: Number(d.applicationFeeAmount) || 0, registration: Number(d.registrationFeeAmount) || 0 });
    }, () => {});
    return () => unsub();
  }, []);

  /* ---- subscribe to the app doc ---- */
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'reportApplications', applicationId),
      (snap) => {
        if (!snap.exists()) {
          setApp(null);
          setLoading(false);
          return;
        }
        const data = { id: snap.id, ...snap.data() } as ApplicationRecord;
        setApp(data);
        // Only seed draft from server when not dirty (so user edits aren't clobbered)
        setDraftDecisions((prev) => (dirty ? prev : data.decisions ?? {}));
        setLoading(false);
      },
      (err) => {
        console.error('App subscribe failed', err);
        setLoading(false);
      },
    );
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  /* ---- subjects are no longer chosen at admission; they are set on
     the pupil's profile (Subjects tab) after admission. ---- */

  const eligibleClasses = useMemo(() => {
    if (!app) return classes;
    // Show classes for the applied-for form first, then others.
    return [...classes].sort((a, b) => {
      const aMatch = a.form === app.applyingForForm ? 0 : 1;
      const bMatch = b.form === app.applyingForForm ? 0 : 1;
      if (aMatch !== bMatch) return aMatch - bMatch;
      return compareClasses(a, b);
    });
  }, [app, classes]);

  const missingDecisions = useMemo(() => {
    const missing: string[] = [];
    if (!draftDecisions.assignedClassId) missing.push('class');
    return missing;
  }, [draftDecisions]);

  /* THE PAPERWORK GATE.
   *
   * The office collects a birth certificate, a guardian's ID, the last
   * school report and a transfer letter or proof of residence. Until all
   * four are attached, the application cannot be printed and cannot be
   * approved — one sentence driving both, so two screens never tell the
   * office a different story about the same application. */
  const attachedDocuments: AttachedDocument[] = app?.documents ?? [];
  /* Removing a document is senior-only, mirroring storage.rules: a wrong
     file on a child's record has to be removable, but one the office has
     already relied on should not quietly vanish. */
  const canDeleteDocuments = (userData?.roles ?? [userData?.role])
    .some((r) => ['admin', 'systemAdmin', 'headteacher', 'admissions'].includes(String(r)));
  const documentsOutstanding = documentsProblem(attachedDocuments);

  /* THE ENTRANCE TEST GATES APPROVAL, NOT PRINTING.
   *
   * The form is printed in order to RUN the test — it is the sheet the
   * marks get written on — so refusing to print until the marks exist
   * would be a circle. Documents are the other way round: the office holds
   * those before anything is printed, so they gate both. */
  const assessmentOutstanding = assessmentProblem(app?.entranceAssessment ?? null);
  const approvingAgainstFail = isOverridingFail(app?.entranceAssessment ?? null);

  const canApprove = missingDecisions.length === 0
    && !documentsOutstanding
    && !assessmentOutstanding;
  const feeReceipts = app?.feeReceipts ?? {};
  const appFeeOk = feeReceipts.application?.received === true;
  const regFeeOk = feeReceipts.registration?.received === true;

  /* ---- family link (prerequisite for admit) ---- */
  const guardianEmail = ((app?.guardians ?? []).find((g) => g.isPrimaryContact) ?? app?.guardians?.[0])?.email ?? '';
  const suggestedFamilies = useMemo(() => {
    const e = guardianEmail.trim().toLowerCase();
    if (!e) return [];
    return families.filter((f) => (f.primaryContact?.email ?? '').trim().toLowerCase() === e);
  }, [families, guardianEmail]);
  const familyMatches = useMemo(() => {
    const q = familyQuery.trim().toLowerCase();
    if (!q) return [];
    return families.filter((f) => f.name.toLowerCase().includes(q)).slice(0, 6);
  }, [families, familyQuery]);
  const familyLink = app?.familyLink;
  const familyOk =
    familyLink?.mode === 'existing'
      ? !!familyLink.familyId
      : familyLink?.mode === 'new'
        ? !!familyLink.familyName?.trim()
        : false;

  // The Admit wizard now enforces the full prerequisite set (up-front
  // fees, family, class & subjects) step-by-step, so the button only
  // needs an approved application that hasn't already been admitted.
  // The fee / family controls below remain as optional pre-staging that
  // the wizard prefills from.
  const canAdmit = app?.status === 'approved' && !app?.admittedStudentId;

  async function setFamilyLink(link: ApplicationFamilyLink | null) {
    if (!app) return;
    await updateDoc(doc(db, 'reportApplications', app.id), {
      familyLink: link ?? deleteField(),
      updatedAt: new Date().toISOString(),
    });
  }

  async function setFeeReceipt(kind: 'application' | 'registration', patch: Partial<AdmissionFeeReceipt>) {
    if (!app) return;
    const cur = app.feeReceipts ?? {};
    const existing = cur[kind] ?? { received: false };
    let merged: AdmissionFeeReceipt = { ...existing, ...patch };
    if (patch.received) {
      merged = {
        ...merged,
        received: true,
        receivedAt: new Date().toISOString(),
        byName: userData?.displayName ?? userData?.email ?? '',
        amount: merged.amount ?? (kind === 'application' ? feeCfg.application : feeCfg.registration),
      };
    }
    await updateDoc(doc(db, 'reportApplications', app.id), {
      feeReceipts: { ...cur, [kind]: merged },
      updatedAt: new Date().toISOString(),
    });
  }

  /* ---- handlers ---- */

  async function saveDraftDecisions(targetStatus?: 'under_review' | 'approved') {
    if (!app) return;
    setSaving(true);
    setMsg(null);
    try {
      const now = new Date().toISOString();
      const decisions: ApplicationDecisions = {
        ...draftDecisions,
        decidedBy: userData?.uid ?? '',
        decidedByName: userData?.displayName ?? '',
        decidedAt: now,
      };
      const patch: Partial<ApplicationRecord> = {
        decisions,
        updatedAt: now,
      };
      if (targetStatus) patch.status = targetStatus;
      else if (app.status === 'pending') patch.status = 'under_review';

      /* An approval made against a failed entrance test is stored as
         exactly that. The school may admit whoever it decides to admit; a
         year later somebody will ask why, and "approved despite the test"
         is a different fact from "approved". */
      if (targetStatus === 'approved' && approvingAgainstFail) {
        patch.approvedAgainstAssessment = true;
      }

      await updateDoc(doc(db, 'reportApplications', app.id), patch);
      setDirty(false);
      setMsg({ ok: true, text: 'Decisions saved.' });
      setTimeout(() => setMsg(null), 2500);
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove() {
    if (documentsOutstanding) {
      setMsg({ ok: false, text: `Cannot approve — documents outstanding. ${documentsOutstanding}` });
      return;
    }
    if (assessmentOutstanding) {
      setMsg({ ok: false, text: `Cannot approve — ${assessmentOutstanding}` });
      return;
    }
    if (!canApprove) {
      setMsg({
        ok: false,
        text: `Cannot approve — missing: ${missingDecisions.join(', ')}.`,
      });
      return;
    }
    if (approvingAgainstFail
      && !window.confirm(
        'This applicant did not pass the entrance test. Approve anyway? '
        + 'The approval will be recorded as having been made against the result.',
      )) {
      return;
    }
    await saveDraftDecisions('approved');
  }

  const [printing, setPrinting] = useState(false);
  async function handlePrint() {
    if (!app) return;
    if (documentsOutstanding) {
      setMsg({ ok: false, text: `Cannot print — documents outstanding. ${documentsOutstanding}` });
      return;
    }
    setPrinting(true);
    try {
      const logo = await loadLogoDataUrl();
      // Editable §7 boilerplate (falls back to defaults inside the builder).
      let formText: AdmissionFormText | null = null;
      try {
        const snap = await getDoc(doc(db, 'reportSchoolSettings', 'admissionFormText'));
        if (snap.exists()) formText = snap.data() as AdmissionFormText;
      } catch {
        /* fall back to defaults */
      }
      const safeSurname = (app.surname || 'applicant').replace(/[^A-Za-z0-9]+/g, '_');
      buildApplicationFormPdf(app, { logoDataUrl: logo, formText }).save(
        `Application_${safeSurname}_${app.referenceNumber || app.id}.pdf`,
      );
    } catch (e) {
      setMsg({ ok: false, text: `Could not build the PDF: ${(e as Error).message}` });
    } finally {
      setPrinting(false);
    }
  }

  function handleAdmit() {
    if (!app) return;
    if (!canAdmit) {
      setMsg({
        ok: false,
        text: 'Approve the application first to enable Admit.',
      });
      return;
    }
    setMsg(null);
    setShowWizard(true);
  }

  async function handleReject() {
    if (!app) return;
    if (!rejectReason.trim()) {
      setMsg({ ok: false, text: 'A reason is required.' });
      return;
    }
    setActionState('rejecting');
    setMsg(null);
    try {
      const now = new Date().toISOString();
      await updateDoc(doc(db, 'reportApplications', app.id), {
        status: 'rejected',
        rejection: {
          reason: rejectReason.trim(),
          rejectedBy: userData?.uid ?? '',
          rejectedByName: userData?.displayName ?? '',
          rejectedAt: now,
        },
        updatedAt: now,
      });
      setShowRejectForm(false);
      setRejectReason('');
      setMsg({ ok: true, text: 'Application marked as rejected.' });
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setActionState('idle');
    }
  }

  if (loading) return <Spinner label="Loading application…" />;
  if (!app) {
    return (
      <SoftCard>
        <div className="text-center py-12">
          <AlertCircle size={32} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm text-gray-500 mb-3">
            Application not found — it may have been admitted or deleted.
          </p>
          <button onClick={onBack} className="text-sm text-primaryLight hover:underline">
            ← Back to list
          </button>
        </div>
      </SoftCard>
    );
  }

  const isTerminal = app.status === 'rejected';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
        >
          <ArrowLeft size={14} />
          Back to applications
        </button>
        <button
          onClick={handlePrint}
          disabled={printing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-primaryLight/30 text-primaryLight hover:bg-primaryLight/5 disabled:opacity-50"
          title="Download a printable, pre-filled copy of the application form"
        >
          {printing ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}
          {printing ? 'Preparing…' : 'Print application'}
        </button>
      </div>

      <GradientHero
        icon={GraduationCap}
        title={`${app.firstName} ${app.surname}`}
        subtitle={
          <span className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-mono">{app.referenceNumber}</span>
            <span>Form {app.applyingForForm} ({app.applyingForYear})</span>
            <span>Submitted {app.submittedAt.slice(0, 10)}</span>
            <StatusPill status={app.status} />
          </span>
        }
      />

      {msg && (
        <div
          className={`p-3 rounded-lg border text-sm flex items-start gap-2 ${
            msg.ok
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-red-50 border-red-200 text-red-700'
          }`}
        >
          {msg.ok ? <CheckCircle2 size={16} className="mt-0.5" /> : <AlertCircle size={16} className="mt-0.5" />}
          <span>{msg.text}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* ---- LEFT: read-only application data (2 cols) ---- */}
        <div className="lg:col-span-2 space-y-6">
          <SoftCard>
            <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
              <User size={16} className="text-primaryLight" /> Student
            </h3>
            <DefList
              items={[
                ['Full name', `${app.firstName} ${app.surname}`],
                ['Gender', app.gender === 'M' ? 'Male' : 'Female'],
                ['Date of birth', app.dateOfBirth],
                ['Nationality', app.nationality],
                ['Home language', app.homeLanguage || '—'],
                ['Religion', app.religion || '—'],
                ['Address', app.address],
                [
                  'Applying for',
                  `Form ${app.applyingForForm} (${app.applyingForYear}, ${app.boardingStatus === 'boarding' ? 'Boarder' : 'Day scholar'})`,
                ],
                ['Previous school', app.previousSchool || '—'],
                ['Notes from applicant', app.notes || '—'],
              ]}
            />
          </SoftCard>

          <SoftCard>
            <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
              <Users size={16} className="text-primaryLight" /> Guardians
            </h3>
            <div className="space-y-3">
              {(app.guardians ?? []).map((g, i) => (
                <div
                  key={g.id ?? i}
                  className={`p-3 rounded-lg border ${
                    g.isPrimaryContact ? 'border-primaryLight/30 bg-primaryLight/5' : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold">{g.fullName}</span>
                    <span className="text-xs text-gray-500">({GUARDIAN_LABEL(g)})</span>
                    {g.isPrimaryContact && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primaryLight text-white">
                        <Star size={10} fill="currentColor" /> Primary
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-gray-600">
                    {g.phone && (
                      <span className="flex items-center gap-1">
                        <Phone size={10} /> {g.phone}
                      </span>
                    )}
                    {g.whatsapp && (
                      <span className="flex items-center gap-1">
                        <MessageCircle size={10} /> {g.whatsapp}
                      </span>
                    )}
                    {g.email && (
                      <span className="flex items-center gap-1">
                        <Mail size={10} /> {g.email}
                      </span>
                    )}
                    {g.occupation && (
                      <span className="flex items-center gap-1">
                        <Briefcase size={10} /> {g.occupation}
                        {g.employer ? ` · ${g.employer}` : ''}
                      </span>
                    )}
                    {g.idNumber && (
                      <span className="flex items-center gap-1 col-span-2">
                        <Globe size={10} /> ID: {g.idNumber}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </SoftCard>

          {(app.medical || app.emergencyContactName) && (
            <SoftCard>
              <h3 className="text-base font-semibold mb-3 flex items-center gap-2">
                <Stethoscope size={16} className="text-primaryLight" /> Medical
              </h3>
              <DefList
                items={[
                  ['Doctor', app.medical?.doctorName ? `${app.medical.doctorName}${app.medical.doctorPhone ? ` · ${app.medical.doctorPhone}` : ''}` : '—'],
                  ['Medical aid', app.medical?.medicalAidProvider ? `${app.medical.medicalAidProvider}${app.medical.medicalAidNumber ? ` (${app.medical.medicalAidNumber})` : ''}` : '—'],
                  ['Allergies', app.medical?.allergies || '—'],
                  ['Chronic conditions', app.medical?.chronicConditions || '—'],
                  ['Medications', app.medical?.medications || '—'],
                  ['Blood type', app.medical?.bloodType || '—'],
                  ['Emergency contact', app.emergencyContactName ? `${app.emergencyContactName} · ${app.emergencyContactPhone}` : '—'],
                ]}
              />
            </SoftCard>
          )}
        </div>

        {/* ---- RIGHT: decisions panel ---- */}
        <div className="space-y-6">
          {/* Above the decisions on purpose: it gates them, so it is read
              before somebody reaches for Approve and is refused. */}
          <EntranceAssessmentPanel
            applicationId={app.id}
            applicantName={`${app.firstName} ${app.surname}`}
            assessment={app.entranceAssessment ?? null}
            canEdit={!isTerminal}
            userData={userData}
          />

          <ApplicationDocuments
            applicationId={app.id}
            applicantName={`${app.firstName} ${app.surname}`}
            documents={attachedDocuments}
            canEdit={!isTerminal}
            canDelete={canDeleteDocuments}
            userData={userData}
          />

          <SoftCard>
            <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
              <Edit3 size={16} className="text-primaryLight" /> Admissions decisions
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Set the class before approving. Subjects are assigned later on the pupil's profile.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <BookOpen size={12} className="text-gray-400" />
                  Assign to class <span className="text-red-500">*</span>
                </label>
                <select
                  value={draftDecisions.assignedClassId ?? ''}
                  onChange={(e) => {
                    setDraftDecisions((d) => ({ ...d, assignedClassId: e.target.value }));
                    setDirty(true);
                  }}
                  disabled={isTerminal}
                  className={glassFieldClass('text-sm')}
                >
                  <option value="">Select a class…</option>
                  {eligibleClasses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {c.form === app.applyingForForm ? '  ✓' : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Internal notes
                </label>
                <textarea
                  value={draftDecisions.internalNotes ?? ''}
                  onChange={(e) => {
                    setDraftDecisions((d) => ({ ...d, internalNotes: e.target.value }));
                    setDirty(true);
                  }}
                  disabled={isTerminal}
                  rows={3}
                  className={glassFieldClass('text-sm')}
                  placeholder="Not shown to the applicant."
                />
              </div>

              {missingDecisions.length > 0 && !isTerminal && (
                <div className="p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-700 flex items-start gap-1.5">
                  <AlertCircle size={12} className="mt-0.5 shrink-0" />
                  <span>Still needed: {missingDecisions.join(', ')}.</span>
                </div>
              )}

              <div className="flex flex-col gap-2 pt-2">
                {!isTerminal && (
                  <>
                    <AccentButton
                      variant="soft"
                      icon={saving ? Loader2 : Edit3}
                      onClick={() => saveDraftDecisions()}
                      disabled={saving || !dirty}
                    >
                      {saving ? 'Saving…' : 'Save draft'}
                    </AccentButton>
                    <div>
                      <AccentButton
                        icon={CheckCircle2}
                        onClick={handleApprove}
                        disabled={!canApprove || saving}
                        title={
                          canApprove ? undefined : `Missing: ${missingDecisions.join(', ')}`
                        }
                      >
                        {app.status === 'approved' ? 'Re-save approval' : 'Approve'}
                      </AccentButton>
                      {!canApprove && (
                        <p className="text-[10px] text-gray-400 mt-1 ml-1">
                          Set {missingDecisions.join(' and ')} first.
                        </p>
                      )}
                    </div>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2.5">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">Family (required to admit)</p>
                      {familyOk ? (
                        <div className="flex items-center justify-between gap-2 text-sm">
                          <span className="text-gray-700 dark:text-gray-200">
                            {familyLink?.mode === 'new' ? 'New family: ' : 'Linked to: '}
                            <span className="font-medium">{familyLink?.familyName}</span>
                            {familyLink?.mode === 'new' && <span className="text-xs text-gray-400 ml-1">(first child — created on admit)</span>}
                          </span>
                          <button onClick={() => setFamilyLink(null)} className="text-xs text-primaryLight hover:underline shrink-0">Change</button>
                        </div>
                      ) : (
                        <div className="space-y-2.5">
                          {suggestedFamilies.length > 0 && (
                            <div>
                              <p className="text-[11px] text-gray-500 mb-1">Suggested (matching guardian email):</p>
                              <div className="flex flex-wrap gap-1.5">
                                {suggestedFamilies.map((f) => (
                                  <button
                                    key={f.id}
                                    onClick={() => setFamilyLink({ mode: 'existing', familyId: f.id, familyName: f.name })}
                                    className="text-xs px-2 py-1 rounded-full bg-primaryLight/10 text-primaryLight hover:bg-primaryLight/20"
                                  >
                                    Link to {f.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <div>
                            <p className="text-[11px] text-gray-500 mb-1">Or search an existing family:</p>
                            <input
                              value={familyQuery}
                              onChange={(e) => setFamilyQuery(e.target.value)}
                              placeholder="Family name…"
                              className="w-full text-sm px-2 py-1 rounded border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
                            />
                            {familyMatches.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1.5">
                                {familyMatches.map((f) => (
                                  <button
                                    key={f.id}
                                    onClick={() => { setFamilyLink({ mode: 'existing', familyId: f.id, familyName: f.name }); setFamilyQuery(''); }}
                                    className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200"
                                  >
                                    {f.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="pt-1 border-t border-gray-100 dark:border-gray-700">
                            <p className="text-[11px] text-gray-500 mb-1 mt-1.5">First child of a new family? Create one:</p>
                            <div className="flex gap-1.5">
                              <input
                                value={newFamilyName}
                                onChange={(e) => setNewFamilyName(e.target.value)}
                                placeholder={`${app.surname} Family`}
                                className="flex-1 text-sm px-2 py-1 rounded border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
                              />
                              <button
                                onClick={() => setFamilyLink({ mode: 'new', familyName: (newFamilyName.trim() || `${app.surname} Family`) })}
                                className="text-xs px-3 py-1 rounded-lg bg-primary text-white hover:bg-primaryLight shrink-0"
                              >
                                Create
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2.5">
                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">Fees received (required to admit)</p>
                      {([
                        { kind: 'application' as const, label: 'Application fee', amount: feeCfg.application, ok: appFeeOk, rx: feeReceipts.application },
                        { kind: 'registration' as const, label: 'Registration fee', amount: feeCfg.registration, ok: regFeeOk, rx: feeReceipts.registration },
                      ]).map((row) => (
                        <div key={row.kind} className="space-y-1">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={row.ok}
                              onChange={(e) => setFeeReceipt(row.kind, { received: e.target.checked })}
                              className="rounded border-gray-300"
                            />
                            <span className="text-gray-700 dark:text-gray-200">{row.label}</span>
                            <span className="text-xs text-gray-400">P{(row.amount || 0).toLocaleString()}</span>
                          </label>
                          {row.ok && (
                            <input
                              value={row.rx?.reference ?? ''}
                              onChange={(e) => setFeeReceipt(row.kind, { reference: e.target.value })}
                              placeholder="Receipt / reference no."
                              className="w-full text-xs px-2 py-1 rounded border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
                            />
                          )}
                        </div>
                      ))}
                      <div className="pt-1">
                        <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                          Charge % of this term's tuition (mid-year joiners)
                        </label>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={termPct}
                          onChange={(e) => setTermPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                          className="w-24 text-sm px-2 py-1 rounded border border-gray-200 dark:border-gray-700 dark:bg-gray-800"
                        />
                        <span className="text-xs text-gray-400 ml-2">% — defaults to full term. One-time fees are always charged in full.</span>
                      </div>
                    </div>

                    <div>
                      <AccentButton
                        icon={UserPlus}
                        onClick={handleAdmit}
                        disabled={!canAdmit}
                        title={
                          canAdmit ? undefined : 'Approve first to enable Admit'
                        }
                      >
                        Admit student
                      </AccentButton>
                      {!canAdmit && app.status !== 'approved' && (
                        <p className="text-[10px] text-gray-400 mt-1 ml-1">
                          {app.status === 'pending' || app.status === 'under_review'
                            ? 'Approve the application first.'
                            : 'Not available.'}
                        </p>
                      )}
                      {canAdmit && (
                        <p className="text-[10px] text-emerald-600 mt-1 ml-1 flex items-center gap-1">
                          <CheckCircle2 size={10} />
                          Ready to admit — will assign next admission number.
                        </p>
                      )}
                    </div>
                  </>
                )}

                <button
                  onClick={() => setShowRejectForm((v) => !v)}
                  disabled={isTerminal}
                  className="text-xs text-red-600 hover:underline disabled:opacity-50"
                >
                  {showRejectForm ? 'Cancel reject' : 'Reject application'}
                </button>

                {showRejectForm && !isTerminal && (
                  <div className="space-y-2 p-3 rounded-lg bg-red-50 border border-red-200">
                    <label className="block text-xs font-medium text-red-700">
                      Reason for rejection
                    </label>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={2}
                      className="w-full text-sm rounded border border-red-200 bg-white px-2 py-1.5 focus:outline-none focus:border-red-400"
                    />
                    <button
                      onClick={handleReject}
                      disabled={actionState === 'rejecting'}
                      className="w-full px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
                    >
                      {actionState === 'rejecting' ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                      Confirm reject
                    </button>
                  </div>
                )}

                {isTerminal && app.rejection && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700">
                    <p className="font-semibold mb-1">Rejected</p>
                    <p>{app.rejection.reason}</p>
                    <p className="mt-1 text-red-500">
                      By {app.rejection.rejectedByName} on{' '}
                      {app.rejection.rejectedAt.slice(0, 10)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </SoftCard>
        </div>
      </div>

      {showWizard && (
        <AdmitWizard
          app={app}
          termFeeFactor={Math.max(0, Math.min(1, (Number(termPct) || 0) / 100))}
          onClose={() => setShowWizard(false)}
          onAdmitted={(_studentId, admissionNumber) => {
            setShowWizard(false);
            setMsg({ ok: true, text: `Admitted as ${admissionNumber}. Student record created.` });
            setTimeout(() => onBack(), 1200);
          }}
        />
      )}
    </div>
  );
}

function DefList({ items }: { items: [string, string][] }) {
  return (
    <dl className="divide-y divide-gray-100 dark:divide-gray-700">
      {items.map(([label, value], i) => (
        <div key={i} className="grid grid-cols-3 gap-3 py-2 text-sm">
          <dt className="text-gray-500">{label}</dt>
          <dd className="col-span-2 text-gray-900 dark:text-white">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function StatusPill({ status }: { status: ApplicationRecord['status'] }) {
  const labels: Record<string, { text: string; cls: string }> = {
    pending: { text: 'New', cls: 'bg-amber-100 text-amber-700' },
    under_review: { text: 'In review', cls: 'bg-sky-100 text-sky-700' },
    approved: { text: 'Approved', cls: 'bg-emerald-100 text-emerald-700' },
    rejected: { text: 'Rejected', cls: 'bg-red-100 text-red-700' },
    duplicate: { text: 'Duplicate', cls: 'bg-gray-100 text-gray-600' },
  };
  /* Same reasoning as the list: an unrecognised status is shown as itself
     rather than crashing the screen that was opened to look at it. */
  const m = labels[status] ?? { text: status ? String(status) : 'Unknown', cls: 'bg-gray-100 text-gray-600' };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}
    >
      {m.text}
    </span>
  );
}
